import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardSection } from '@/components/dashboard/DashboardSection';
import { IntelligentInbox } from '@/components/inbox/IntelligentInbox';
import { Badge } from '@/components/ui/badge';
import { useCommunications } from '@/context/CommunicationsContext';
import { useDashboardSearch } from '@/hooks/useDashboardSearch';
function validationFromSearch(search) {
    const v = new URLSearchParams(search).get('validation');
    if (v === 'pending' || v === 'validated' || v === 'escalated')
        return v;
    return 'all';
}
const pageContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.07, delayChildren: 0.04 },
    },
};
const pageItem = {
    hidden: { opacity: 0, y: 12 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
    },
};
export function InboxPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { communications, error, } = useCommunications();
    const { search, setSearch } = useDashboardSearch();
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [validationFilter, setValidationFilter] = useState(() => validationFromSearch(searchParams.toString()));
    useEffect(() => {
        const next = validationFromSearch(searchParams.toString());
        const t = window.setTimeout(() => {
            setValidationFilter(next);
        }, 0);
        return () => window.clearTimeout(t);
    }, [searchParams]);
    const onValidationFilter = useCallback((v) => {
        setValidationFilter(v);
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            if (v === 'all')
                p.delete('validation');
            else
                p.set('validation', v);
            return p;
        }, { replace: true });
    }, [setSearchParams]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return communications.filter((c) => {
            if (categoryFilter !== 'all' &&
                c.category !== categoryFilter &&
                !(c.categories || []).includes(categoryFilter)) {
                return false;
            }
            if (validationFilter !== 'all' && c.validationStatus !== validationFilter) {
                return false;
            }
            if (!q)
                return true;
            return (c.subject.toLowerCase().includes(q) ||
                c.bodyPreview.toLowerCase().includes(q) ||
                c.sender.address.toLowerCase().includes(q) ||
                c.sender.name.toLowerCase().includes(q));
        });
    }, [communications, search, categoryFilter, validationFilter]);
    const listAside = useMemo(() => (_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsxs(Badge, { variant: "secondary", className: "rounded-md px-2.5 py-1 text-xs font-medium tabular-nums", children: [filtered.length, " shown"] }), _jsxs("span", { className: "hidden text-xs text-muted-foreground sm:inline", children: ["of ", communications.length, " loaded"] })] })), [filtered.length, communications.length]);
    return (_jsxs(motion.div, { className: "mx-auto flex w-full max-w-[1400px] flex-col gap-10 pb-10", variants: pageContainer, initial: "hidden", animate: "show", children: [_jsx(motion.div, { variants: pageItem, children: _jsx(DashboardSection, { eyebrow: "Workspace", title: "Intelligent inbox", description: "Search the bar above or use the fields below, then open any row for AI insight, re-classification, and escalation. Filters stay in the URL so you can share a view.", aside: listAside }) }), error ? (_jsx(motion.div, { variants: pageItem, role: "status", className: error.includes('0 messages')
                    ? 'rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 shadow-sm dark:text-amber-50'
                    : 'rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive shadow-sm', children: error })) : null, _jsx(motion.div, { variants: pageItem, className: "min-w-0", children: _jsx(IntelligentInbox, { items: filtered, search: search, onSearchChange: setSearch, categoryFilter: categoryFilter, onCategoryFilter: setCategoryFilter, validationFilter: validationFilter, onValidationFilter: onValidationFilter, onSelect: (id) => navigate(`/inbox/${encodeURIComponent(id)}`), showSearchField: true }) })] }));
}
